/* Clavia Projector Room Planner — logic only, no DOM.
   Works in the browser (window.ClaviaPlanner) and in Node (module.exports).
   All numbers come from clavia-planner-config.json; nothing is hardcoded here. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.ClaviaPlanner = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var FT_PER_M = 3.28084;

  function round(n, d) { var p = Math.pow(10, d || 0); return Math.round(n * p) / p; }

  /* Optional localized text (the Shopify theme passes its locale strings in cfg.text):
     cfg.text = { reason: { <rule id>: '...' }, advice: { too_small, wall, light, model_max, fallback }, decimal: ',' }
     Advice templates use {tokens}. Without cfg.text the built-in EN/FR strings are used. */
  function fmtNum(n, cfg) { var s = String(n); return cfg.text && cfg.text.decimal ? s.replace('.', cfg.text.decimal) : s; }
  function tpl(cfg, key, vars) {
    var t = cfg.text && cfg.text.advice && cfg.text.advice[key];
    if (!t) return null;
    return t.replace(/\{(\w+)\}/g, function (m, k) { return vars[k] != null ? fmtNum(vars[k], cfg) : m; });
  }

  /* Picture diagonal (inches) from lens-to-wall distance (m) for a given throw ratio. */
  function diagonalFromDistance(distance_m, cfg) {
    var g = cfg.geometry;
    var width_m = distance_m / (g.throw_ratio);
    var diag_m = width_m / g.width_over_diagonal;
    return diag_m * g.inch_per_metre;
  }

  /* Distance (m) needed for a given diagonal (inches). Inverse of the above. */
  function distanceForDiagonal(diag_in, cfg) {
    var g = cfg.geometry;
    var width_m = (diag_in / g.inch_per_metre) * g.width_over_diagonal;
    return width_m * g.throw_ratio;
  }

  /* Max diagonal (inches) that fits a wall of a given clear width (m). */
  function diagonalFromWall(wall_w_m, cfg) {
    var g = cfg.geometry;
    return (wall_w_m / g.width_over_diagonal) * g.inch_per_metre;
  }

  function matches(rule, ctx) {
    var w = rule.when || {};
    if (w.light && w.light.indexOf(ctx.light) < 0) return false;
    if (w.placement && w.placement.indexOf(ctx.placement) < 0) return false;
    if (w.use && w.use.indexOf(ctx.use) < 0) return false;
    if (typeof w.diag_gt_in === 'number' && !(ctx.diag_in > w.diag_gt_in)) return false;
    return true;
  }

  /* Clamp a diagonal to what a model can produce, and say why it was clamped. */
  function fitToModel(diag_in, model, capOverride) {
    var out = { diag_in: diag_in, capped_by: null, too_small: false };
    var max = model.size_max_in;
    if (typeof capOverride === 'number' && capOverride > 0 && capOverride < max) max = capOverride;
    if (diag_in > max) { out.diag_in = max; out.capped_by = capOverride && max === capOverride ? 'light' : 'model_max'; }
    if (diag_in < model.size_min_in) { out.too_small = true; out.diag_in = model.size_min_in; }
    return out;
  }

  /*
    inputs = {
      distance_m: number (required),
      wall_w_m:   number or null,
      light:      'blackout' | 'curtains' | 'daylight',
      placement:  'shelf' | 'ceiling' | 'portable',
      use:        'movies' | 'sports' | 'gaming' | 'kids',
      lang:       'en' | 'fr'
    }
  */
  function result(inputs, cfg) {
    var lang = inputs.lang === 'fr' ? 'fr' : 'en';
    var raw = diagonalFromDistance(inputs.distance_m, cfg);
    var wallMax = inputs.wall_w_m ? diagonalFromWall(inputs.wall_w_m, cfg) : Infinity;
    var roomDiag = Math.min(raw, wallMax);
    var roomCap = raw > wallMax ? 'wall' : null;

    var ctx = { light: inputs.light, placement: inputs.placement, use: inputs.use, diag_in: roomDiag };
    var rule = null;
    for (var i = 0; i < cfg.rules.length; i++) { if (matches(cfg.rules[i], ctx)) { rule = cfg.rules[i]; break; } }
    if (!rule) rule = cfg.rules[cfg.rules.length - 1];

    var pickKey = rule.pick, altKey = rule.alt;
    var pick = cfg.models[pickKey];
    var fitPick = fitToModel(roomDiag, pick, rule.cap_in);

    /* If the room is too small for the pick (below its minimum), fall back to the model with the smaller minimum. */
    var fallback = null;
    if (fitPick.too_small) {
      var keys = Object.keys(cfg.models).sort(function (a, b) { return cfg.models[a].size_min_in - cfg.models[b].size_min_in; });
      var smallest = keys[0];
      if (smallest !== pickKey && roomDiag >= cfg.models[smallest].size_min_in) {
        fallback = { from: pickKey, to: smallest };
        altKey = pickKey; pickKey = smallest; pick = cfg.models[pickKey];
        fitPick = fitToModel(roomDiag, pick, rule.cap_in);
      }
    }

    var alt = altKey ? cfg.models[altKey] : null;
    var fitAlt = alt ? fitToModel(roomDiag, alt, null) : null;
    if (fitAlt && fitAlt.too_small) { alt = null; fitAlt = null; }

    var out = {
      lang: lang,
      inputs: inputs,
      raw_diag_in: round(raw, 1),
      wall_max_in: isFinite(wallMax) ? round(wallMax, 1) : null,
      room_diag_in: round(roomDiag, 1),
      room_capped_by: roomCap,
      rule: rule.id,
      reason: (cfg.text && cfg.text.reason && cfg.text.reason[rule.id]) || rule.reason[lang],
      pick: {
        key: pickKey, model: pick,
        diag_in: Math.round(fitPick.diag_in),
        capped_by: fitPick.capped_by,
        too_small: fitPick.too_small,
        width_cm: round(fitPick.diag_in * cfg.geometry.width_over_diagonal * 2.54, 0),
        height_cm: round(fitPick.diag_in * cfg.geometry.height_over_diagonal * 2.54, 0)
      },
      alt: alt ? {
        key: altKey, model: alt,
        diag_in: Math.round(fitAlt.diag_in),
        capped_by: fitAlt.capped_by
      } : null,
      fallback: fallback,
      advice: []
    };

    /* Plain-language advice lines the result card can show. */
    var g = cfg.geometry;
    if (out.pick.too_small) {
      var need = distanceForDiagonal(pick.size_min_in, cfg);
      out.advice.push(tpl(cfg, 'too_small', { d: round(inputs.distance_m, 2), model: pick.short, min: pick.size_min_in, need: round(need, 2) }) || (lang === 'fr'
        ? 'À ' + round(inputs.distance_m, 2) + ' m, l\'image est plus petite que le minimum du ' + pick.short + ' (' + pick.size_min_in + ' po). Reculez le projecteur à au moins ' + round(need, 2) + ' m.'
        : 'At ' + round(inputs.distance_m, 2) + ' m the picture is smaller than the ' + pick.short + '\'s minimum (' + pick.size_min_in + ' in). Move the projector back to at least ' + round(need, 2) + ' m.'));
    }
    if (roomCap === 'wall') {
      out.advice.push(tpl(cfg, 'wall', { wall: Math.round(wallMax), raw: Math.round(raw) }) || (lang === 'fr'
        ? 'Votre mur limite l\'image à environ ' + Math.round(wallMax) + ' po même si la distance permettrait ' + Math.round(raw) + ' po.'
        : 'Your wall limits the picture to about ' + Math.round(wallMax) + ' in even though the distance would allow ' + Math.round(raw) + ' in.'));
    }
    if (out.pick.capped_by === 'light') {
      out.advice.push(tpl(cfg, 'light', { cap: out.pick.diag_in, night: Math.round(roomDiag) }) || (lang === 'fr'
        ? 'En plein jour, gardez l\'image à ' + out.pick.diag_in + ' po ou moins pour conserver le contraste. Le soir, le même recul donne ' + Math.round(roomDiag) + ' po.'
        : 'In daylight, keep the picture at ' + out.pick.diag_in + ' in or less to hold contrast. At night the same distance gives you ' + Math.round(roomDiag) + ' in.'));
    }
    if (out.pick.capped_by === 'model_max') {
      out.advice.push(tpl(cfg, 'model_max', { model: pick.short, max: pick.size_max_in, dist: round(distanceForDiagonal(pick.size_max_in, cfg), 2) }) || (lang === 'fr'
        ? 'Le ' + pick.short + ' plafonne à ' + pick.size_max_in + ' po ; rapprochez-le à ' + round(distanceForDiagonal(pick.size_max_in, cfg), 2) + ' m pour une image nette.'
        : 'The ' + pick.short + ' tops out at ' + pick.size_max_in + ' in; bring it in to ' + round(distanceForDiagonal(pick.size_max_in, cfg), 2) + ' m for a sharp picture.'));
    }
    if (fallback) {
      out.advice.push(tpl(cfg, 'fallback', { from: cfg.models[fallback.from].short, min: cfg.models[fallback.from].size_min_in, model: pick.short }) || (lang === 'fr'
        ? 'Le ' + cfg.models[fallback.from].short + ' a besoin d\'au moins ' + cfg.models[fallback.from].size_min_in + ' po ; à cette distance, le ' + pick.short + ' est le seul qui convient.'
        : 'The ' + cfg.models[fallback.from].short + ' needs at least ' + cfg.models[fallback.from].size_min_in + ' in; at this distance the ' + pick.short + ' is the one that fits.'));
    }
    return out;
  }

  /*
    Wall-marker geometry for a 16:9 picture of a given diagonal (inches).
    Returns sizes in cm and inches, the four corner positions relative to the
    bottom-left corner, and the letter pages needed to print the markers.
  */
  function markers(diag_in, cfg) {
    var g = cfg.geometry, m = cfg.markers;
    var w_in = diag_in * g.width_over_diagonal;
    var h_in = diag_in * g.height_over_diagonal;
    var w_cm = w_in * 2.54, h_cm = h_in * 2.54;
    return {
      diag_in: Math.round(diag_in),
      width_cm: round(w_cm, 1), height_cm: round(h_cm, 1),
      width_in: round(w_in, 1), height_in: round(h_in, 1),
      corners: [
        { id: 'bl', label: { en: 'Bottom left', fr: 'Bas gauche' }, x_cm: 0, y_cm: 0, note: { en: 'Start here. Tape it ' + m.recommended_bottom_edge_cm.min + ' to ' + m.recommended_bottom_edge_cm.max + ' cm above the floor for seated viewing.', fr: 'Commencez ici. Collez-le à ' + m.recommended_bottom_edge_cm.min + ' à ' + m.recommended_bottom_edge_cm.max + ' cm du sol pour un visionnement assis.' } },
        { id: 'br', label: { en: 'Bottom right', fr: 'Bas droite' }, x_cm: round(w_cm, 1), y_cm: 0, note: { en: 'Measure ' + round(w_cm, 1) + ' cm to the right of the bottom-left marker, same height.', fr: 'Mesurez ' + round(w_cm, 1) + ' cm à droite du repère bas gauche, même hauteur.' } },
        { id: 'tl', label: { en: 'Top left', fr: 'Haut gauche' }, x_cm: 0, y_cm: round(h_cm, 1), note: { en: 'Measure ' + round(h_cm, 1) + ' cm straight up from the bottom-left marker.', fr: 'Mesurez ' + round(h_cm, 1) + ' cm droit au-dessus du repère bas gauche.' } },
        { id: 'tr', label: { en: 'Top right', fr: 'Haut droite' }, x_cm: round(w_cm, 1), y_cm: round(h_cm, 1), note: { en: 'Measure ' + round(h_cm, 1) + ' cm straight up from the bottom-right marker.', fr: 'Mesurez ' + round(h_cm, 1) + ' cm droit au-dessus du repère bas droite.' } }
      ],
      pages: m.pages,
      paper: m.paper,
      calibration_bar_cm: m.calibration_bar_cm,
      distance_m: round(distanceForDiagonal(diag_in, cfg), 2)
    };
  }

  function toFeet(m) { return round(m * FT_PER_M, 1); }
  function toMetres(ft) { return round(ft / FT_PER_M, 2); }

  return {
    diagonalFromDistance: diagonalFromDistance,
    distanceForDiagonal: distanceForDiagonal,
    diagonalFromWall: diagonalFromWall,
    result: result,
    markers: markers,
    toFeet: toFeet,
    toMetres: toMetres
  };
});
